import type { Competition, KoRound, Match, Phase, Slot, Team, Tournament } from '@airmail/engine';
import { t } from '@/i18n';

export type TeamLookup = (id: string) => Team | undefined;

export function teamLookup(comp: Competition): TeamLookup {
  const map = new Map(comp.teams.map((t) => [t.id, t]));
  return (id) => map.get(id);
}

/** Anzeigename eines Slots: Team-Kurzname oder Platzhalter („Sieger Spiel 12") */
export function slotLabel(slot: Slot, lookup: TeamLookup, matches: readonly Match[], short = false): string {
  switch (slot.kind) {
    case 'team': {
      // Wechselnde Partner: „Ferrero / Blangero“ — zwei Einzelspieler als Doppel für dieses Spiel
      const name = (id: string) => {
        const t = lookup(id);
        return t ? (short ? t.shortName : t.name) : '?';
      };
      return slot.partnerId ? `${name(slot.teamId)} / ${name(slot.partnerId)}` : name(slot.teamId);
    }
    case 'winnerOf':
      return t('Sieger {m}', { m: matchRef(slot.matchId, matches) });
    case 'loserOf':
      return t('Verlierer {m}', { m: matchRef(slot.matchId, matches) });
    case 'bye':
      return t('Freilos');
    case 'tbd':
      return slot.label;
  }
}

function matchRef(id: string, matches: readonly Match[]): string {
  const m = matches.find((x) => x.id === id);
  return m ? `#${m.number}` : id;
}

/**
 * Team eines Slots. Bei wechselnden Partnern ein Doppel nur für dieses Spiel:
 * Name „Ferrero / Blangero“, beide Spieler — so zeigen Ergebnis-Dialog,
 * Spielkarten und Live-Stand beide, ohne dass jede Stelle davon wissen muss.
 */
export function slotTeam(slot: Slot, lookup: TeamLookup): Team | undefined {
  if (slot.kind !== 'team') return undefined;
  const team = lookup(slot.teamId);
  if (!slot.partnerId || !team) return team;
  const partner = lookup(slot.partnerId);
  if (!partner) return team;
  return { ...team, name: `${team.name} / ${partner.name}`, shortName: `${team.shortName} / ${partner.shortName}`, players: [...team.players, ...partner.players] };
}

/** Partner eines Spielers in diesem Spiel (wechselnde Partner), sonst undefined */
export function partnerIn(m: Match, teamId: string, lookup: TeamLookup): Team | undefined {
  for (const s of [m.a, m.b]) {
    if (s.kind !== 'team' || !s.partnerId) continue;
    if (s.teamId === teamId) return lookup(s.partnerId);
    if (s.partnerId === teamId) return lookup(s.teamId);
  }
  return undefined;
}

export function phaseOfMatch(comp: Competition, m: Match): Phase | undefined {
  return comp.phases.find((p) => p.id === m.phaseId);
}

/** „Gruppe B · Runde 3" bzw. „Viertelfinale" */
export function matchContext(comp: Competition, m: Match): string {
  const phase = phaseOfMatch(comp, m);
  if (!phase) return '';
  if (phase.type === 'groups') {
    const g = phase.groups.find((x) => x.id === m.groupId);
    return t('Gruppe {g} · Runde {r}', { g: g?.name ?? '?', r: m.round });
  }
  if (phase.bracket) {
    if (m.bracketRole === 'third') return t('Spiel um Platz 3');
    return roundName(t, phase.bracket.rounds, m.round);
  }
  return t('Runde {n}', { n: m.round });
}

type Uebersetzer = (de: string, vars?: Record<string, string | number>) => string;

/**
 * Name einer Bracket-Runde aus „Runden gesamt" und Rundennummer: Finale,
 * Halbfinale, Viertelfinale, Achtelfinale, davor „Runde n". Die eine Stelle
 * für KO-Seite, Beamer, Druck und `matchContext` — vorher viermal, mit drei
 * verschiedenen Fallback-Schlüsseln.
 */
export function roundName(t: Uebersetzer, rounds: number, r: number): string {
  const left = rounds - r;
  return left === 0 ? t('Finale') : left === 1 ? t('Halbfinale') : left === 2 ? t('Viertelfinale') : left === 3 ? t('Achtelfinale') : t('Runde {n}', { n: r });
}

/** Name einer von Hand gestarteten KO-Runde (docs/notes/ko-runden.md): eigener Name der Leitung, sonst „Runde n" */
export function koRoundLabel(t: Uebersetzer, r: KoRound): string {
  return r.label ?? t('Runde {n}', { n: r.number });
}

/** „Piazza · Feld 1" — Platz (Feldgruppe) vor dem Feldnamen, damit Spieler wissen, wohin (docs/17) */
/**
 * Anzeigename eines Feldes. Felder heißen gespeichert so, wie die Leitung sie
 * angelegt hat — bei einer deutschen Leitung „Feld 1". Harald (10/2026): In
 * der italienischen Zuschauer-Ansicht stand deshalb „Feld" statt „Campo".
 * Die **automatisch vergebenen** Namen („Feld 3", „Court 3", „Campo 3")
 * erscheinen deshalb in der Sprache des Betrachters; eigene Namen wie
 * „Piazza 1" oder „Halle links" bleiben, wie sie sind.
 */
const STANDARD_FELD = /^(?:Feld|Court|Campo)\s+(\d+)$/i;
export function feldName(name: string): string {
  const m = STANDARD_FELD.exec(name.trim());
  return m ? t('Feld {n}', { n: Number(m[1]) }) : name;
}

export function courtLabel(turnier: Tournament, courtId: string | undefined): string | undefined {
  const c = turnier.courts.find((x) => x.id === courtId);
  if (!c) return undefined;
  return c.group ? `${c.group} · ${feldName(c.name)}` : feldName(c.name);
}

export function scoreText(m: Match): string {
  if (!m.result) return '';
  if (m.result.sets.length === 1) {
    const s = m.result.sets[0];
    return s ? `${s.a}:${s.b}` : '';
  }
  return m.result.sets.map((s) => `${s.a}:${s.b}`).join(' · ');
}
